import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Building,
  Phone,
  Mail,
  Star,
  RefreshCw,
  RotateCcw,
  Users,
  BookOpen,
  GraduationCap,
  AlertTriangle,
  Check
} from 'lucide-react';

const LocationSettings = () => {
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    contact_email: '',
    contact_phone: '',
    is_primary: false,
    is_active: true
  });

  useEffect(() => {
    fetchLocations();
    fetchStats();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations/?include_inactive=true');
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/locations/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const getStatsForLocation = (locationId) => {
    return stats.find(s => s.location_id === locationId) || {};
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      contact_email: '',
      contact_phone: '',
      is_primary: false,
      is_active: true
    });
    setEditingLocation(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || '',
      address_line1: location.address_line1 || '',
      address_line2: location.address_line2 || '',
      city: location.city || '',
      state: location.state || '',
      zip_code: location.zip_code || '',
      country: location.country || 'USA',
      contact_email: location.contact_email || '',
      contact_phone: location.contact_phone || '',
      is_primary: location.is_primary || false,
      is_active: location.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, formData);
        toast.success('Location updated successfully');
      } else {
        await api.post('/locations/', formData);
        toast.success('Location created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!locationToDelete) return;
    
    try {
      await api.delete(`/locations/${locationToDelete.id}`);
      toast.success('Location deactivated');
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      fetchLocations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  const handleRestore = async (location) => {
    try {
      await api.post(`/locations/${location.id}/restore`);
      toast.success('Location restored');
      fetchLocations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore location');
    }
  };

  const activeLocations = locations.filter(l => l.is_active);
  const inactiveLocations = locations.filter(l => !l.is_active);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="location-settings-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building className="h-7 w-7 text-blue-600" />
              Location Management
            </h1>
            <p className="text-slate-600 mt-1">
              Manage academy locations across different areas
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button onClick={openAddDialog} data-testid="add-location-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Active Locations</p>
                  <p className="text-2xl font-bold">{activeLocations.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BookOpen className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Programs</p>
                  <p className="text-2xl font-bold">
                    {stats.reduce((sum, s) => sum + s.program_count, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Coaches</p>
                  <p className="text-2xl font-bold">
                    {stats.reduce((sum, s) => sum + s.coach_count, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Users className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Active Enrollments</p>
                  <p className="text-2xl font-bold">
                    {stats.reduce((sum, s) => sum + s.active_enrollment_count, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Locations Table */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Academy Locations
            </CardTitle>
            <CardDescription>
              {activeLocations.length} active location(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : activeLocations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No locations configured</p>
                <p className="text-sm mt-1">Add your first location to get started</p>
                <Button onClick={openAddDialog} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Location
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-center">Programs</TableHead>
                    <TableHead className="text-center">Coaches</TableHead>
                    <TableHead className="text-center">Enrollments</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeLocations.map((location) => {
                    const locStats = getStatsForLocation(location.id);
                    return (
                      <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{location.name}</span>
                            {location.is_primary && (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Star className="h-3 w-3 mr-1" />
                                Primary
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">
                          {location.full_address}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Mail className="h-3 w-3" />
                              {location.contact_email}
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Phone className="h-3 w-3" />
                              {location.contact_phone}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{locStats.program_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{locStats.coach_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{locStats.active_enrollment_count || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(location)}
                              data-testid={`edit-location-${location.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setLocationToDelete(location);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-location-${location.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Inactive Locations */}
        {inactiveLocations.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-slate-600">Inactive Locations</CardTitle>
              <CardDescription>
                These locations have been deactivated
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inactiveLocations.map((location) => (
                    <TableRow key={location.id} className="opacity-60">
                      <TableCell className="font-medium">{location.name}</TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {location.full_address}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(location)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </DialogTitle>
              <DialogDescription>
                {editingLocation ? 'Update location details' : 'Add a new academy location'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Location Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Edison Main, Piscataway Branch"
                  required
                  data-testid="location-name-input"
                />
              </div>

              <div>
                <Label htmlFor="address_line1">Address Line 1 *</Label>
                <Input
                  id="address_line1"
                  value={formData.address_line1}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  placeholder="Street address"
                  required
                />
              </div>

              <div>
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  value={formData.address_line2}
                  onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  placeholder="Suite, unit, building (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="NJ"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    placeholder="08820"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact_email">Contact Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="location@yourdomain.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="contact_phone">Contact Phone *</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="732-555-0100"
                  required
                />
              </div>

              <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="is_primary" className="font-medium">Primary Location</Label>
                  <p className="text-sm text-slate-600">Set as the main academy location</p>
                </div>
                <Switch
                  id="is_primary"
                  checked={formData.is_primary}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_primary: checked })}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  {editingLocation ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Deactivate Location?
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to deactivate "{locationToDelete?.name}"?
              </DialogDescription>
            </DialogHeader>

            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Note</AlertTitle>
              <AlertDescription className="text-amber-700">
                This will hide the location from selection. You can restore it later.
                Make sure no active programs are assigned to this location.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Deactivate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LocationSettings;
